



































































import ContentCard from '@/components/ContentCard.vue';
import { computed, defineComponent, Ref, ref, watch } from '@vue/composition-api';
import { AuthorityModel, useGetAuthoritiesAndDevicesCountQuery } from '@/graphql/types';
import { S2Selection } from '@/types/s2selection';
import Select2 from '@/components/Select2.vue';
import useDefaultStatusS2Selections from '@/composables/useDefaultStatusS2Selections';
import useNavigateToAuthority from '@/composables/useNavigateToAuthority';
import { FilterState } from '@/types/enums';
import Table from '@/components/Table.vue';
import { TableField } from '@/types/TableField';
import { useResult } from '@vue/apollo-composable';

export default defineComponent({
  components: { ContentCard, Select2, Table },
  setup(_, { root }) {
    const defaultSort = ref({
      sortBy: 'name',
      sortDesc: false
    });
    const perPage = 40;
    const currentPage = ref(1);
    const nameFilter = ref(null as string | null);
    const cvrFilter = ref(null as string | null);
    const statusList = useDefaultStatusS2Selections();
    const statusFilter = ref(null as S2Selection<FilterState> | null);
    statusFilter.value = statusList[0];
    const authorities = ref<any[]>([]);
    const filtered = ref([] as AuthorityModel[]);
    const sortedAuthorityItems = ref<any[]>([]);

    const { result, loading, error, refetch } = useGetAuthoritiesAndDevicesCountQuery({
      fetchPolicy: 'network-only'
    });

    watch(
      () => useResult(result).value,
      newValue => {
        if (newValue) {
          const authorityItems: any[] = [];
          newValue.forEach(a =>
            authorityItems.push({
              id: a.id,
              name: a.name ?? '',
              cvr: a.cvr ?? '',
              isActive: a.isActive,
              deviceCount: a.deviceCount,
              devicesCount: deviceCount(a)
            })
          );
          authorities.value = [...authorityItems];
          setSortedAuthorities();
        }
      },
      { immediate: true }
    );

    watch(
      () => currentPage.value,
      newValue => {
        setSortedAuthorities();
      },
      { immediate: true }
    );

    root.$on('refetchAuthorities', (newAuthority: any) => {
      if (newAuthority) {
        newAuthority.devicesCount = deviceCount(newAuthority);
        authorities.value.push(newAuthority);
        filtered.value = [newAuthority, ...filtered.value];
        setSortedAuthorities(filtered.value);
      } else {
        refetch();
      }
    });

    function useFilteringAuthorities(
      authorities: Ref<AuthorityModel[]>,
      statusFilter: Ref<S2Selection<FilterState> | null>,
      nameFilter: Ref<string | null>,
      cvrFilter: Ref<string | null>
    ) {
      watch([authorities, statusFilter, nameFilter, cvrFilter], () => {
        currentPage.value = 1;
        filter(authorities.value, statusFilter.value?.object ?? null, nameFilter.value, cvrFilter.value);
        setSortedAuthorities(filtered.value);
      });

      async function filter(
        authorities: AuthorityModel[],
        statusFilter: FilterState | null,
        nameFilter: string | null,
        cvrFilter: string | null
      ) {
        filtered.value = authorities.filter(element => {
          var isValid = true;

          if (statusFilter === FilterState.Inactive) {
            isValid = element.isActive === false;
          }

          if (statusFilter === FilterState.Active) {
            isValid = element.isActive === true;
          }

          if (nameFilter && isValid) {
            isValid = element.name.toLowerCase().indexOf(nameFilter.toLowerCase()) >= 0;
          }

          if (cvrFilter && isValid) {
            isValid = element.cvr.toString().indexOf(cvrFilter) >= 0;
          }

          return isValid;
        });
      }
      return filtered;
    }

    function navigateToAuthority(router: any, authority: any) {
      useNavigateToAuthority(router, authority.id);
    }

    function deviceCount(authority: any) {
      return `${authority.deviceCount ?? 0} ${authority.deviceCount > 1 ? ' enheder' : ' enhed'}`;
    }

    function setSortedAuthorities(items: any = null) {
      const asc = (a: any, b: any) => a[defaultSort.value.sortBy]?.localeCompare(b[defaultSort.value.sortBy], 'da');
      const desc = (a: any, b: any) => b[defaultSort.value.sortBy]?.localeCompare(a[defaultSort.value.sortBy], 'da');
      authorities.value = authorities.value.sort(defaultSort.value.sortDesc ? desc : asc);
      sortedAuthorityItems.value = (items ?? authorities.value).slice(
        (currentPage.value - 1) * perPage,
        (currentPage.value - 1) * perPage + perPage
      );
    }

    function onSortChanged($event: any) {
      defaultSort.value = {
        sortBy: $event.sortBy,
        sortDesc: $event.sortDesc
      };
      setSortedAuthorities();
    }

    return {
      loading,
      error,
      authorities: useFilteringAuthorities(authorities, statusFilter, nameFilter, cvrFilter),
      sortedAuthorityItems: computed(() => sortedAuthorityItems.value),
      nameFilter,
      cvrFilter,
      statusFilter,
      statusList,
      useNavigateToAuthority,
      navigateToAuthority,
      deviceCount,
      perPage,
      currentPage,
      onSortChanged,
      fields: computed<TableField[]>(() => [
        {
          key: 'name',
          sortable: true,
          label: root.$tc('authorities.fields.name'),
          width: '35%'
        },
        {
          key: 'cvr',
          sortable: true,
          label: root.$tc('authorities.fields.cvr'),
          width: '35%'
        },
        {
          key: 'devicesCount',
          formatter: (value, key, item) => deviceCount(item),
          sortable: true,
          sortByFormatted: true,
          label: root.$tc('authorities.fields.devices'),
          width: '30%'
        }
      ])
    };
  }
});
